export const PROTOCOL_VERSION = "1" as const;

export type SessionStatus = "starting" | "playing" | "paused" | "draining" | "ended";
export type ConnectionState = "offline" | "connecting" | "connected" | "reconnecting";
export type TimelineEntryState = "staged" | "committed" | "superseded";
export type PlaybackAdapter = "mse" | "hls" | "ios-native" | "android-native" | "simple-queue";
export const MUSIC_GENRES = ["ambient", "electronic", "techno", "drum-and-bass", "cinematic", "jazz", "post-rock"] as const;
export const MUSIC_MOODS = ["calm", "focused", "euphoric", "dark", "dreamy"] as const;
export const INSTRUMENT_PALETTES = ["electronic", "acoustic", "hybrid"] as const;
export type MusicGenre = typeof MUSIC_GENRES[number];
export type MusicMood = typeof MUSIC_MOODS[number];
export type InstrumentPalette = typeof INSTRUMENT_PALETTES[number];
export const BPM_VALUES = [60, 90, 120, 150, 180] as const;
export type Bpm = typeof BPM_VALUES[number];

export function isBpm(value: unknown): value is Bpm {
  return typeof value === "number" && BPM_VALUES.includes(value as Bpm);
}

export interface GenerationControls {
  genre: MusicGenre;
  mood: MusicMood;
  palette: InstrumentPalette;
  tempo_bpm: Bpm;
}

export interface LiveGenerationControls extends GenerationControls {
  energy: number;
}

export interface TimelineEntry {
  entry_id: string;
  sequence: number;
  epoch: number;
  start_ms: number;
  duration_ms: number;
  start_sample?: number;
  state: TimelineEntryState;
  url: string;
  content_hash: string;
  codec: string;
  init_ref?: string;
  media_time_ms?: number;
  media_time_samples?: number;
  media_duration_samples?: number;
  preroll_samples?: number;
  padding_samples?: number;
  source_leading_silence_samples?: number;
  expires_at?: string;
}

export interface TimelineUpdate {
  protocol_version: typeof PROTOCOL_VERSION;
  session_id: string;
  timeline_revision: number;
  replace_from_sequence?: number;
  entries: TimelineEntry[];
}

export interface SessionSnapshot extends TimelineUpdate {
  status: SessionStatus;
  control_epoch: number;
  energy_target: number;
  generated_until_ms: number;
  staged_until_ms: number;
  committed_until_ms: number;
  active_provider_profile: string;
  generation_controls: LiveGenerationControls;
  audible_generation_controls?: LiveGenerationControls;
  pending_generation_controls?: LiveGenerationControls;
  controls_effective_from_ms?: number;
  controls_effective_from_sequence?: number;
  degraded?: { active: boolean; reason?: string };
  startup_catalog?: { id: string; version: string; bpm: Bpm; palette: InstrumentPalette; variant: number };
  generation_status?: { phase: "warming" | "generating" | "ready" | "waiting"; committed_reserve_ms: number };
}

export interface PlaybackTelemetry {
  position_ms: number;
  committed_buffered_ms: number;
  total_buffered_ms: number;
  adapter?: PlaybackAdapter;
  clock_uncertainty_ms?: number;
  background?: boolean;
  events?: PlaybackQoeEvent[];
}

export type PlaybackQoeEventType = "boundary_crossed" | "append_complete" | "stall_started" | "stall_ended" | "gap_detected";

export interface PlaybackQoeEvent {
  type: PlaybackQoeEventType;
  occurred_at: string;
  duration_ms?: number;
  sequence?: number;
  timeline_revision?: number;
}

interface ControlEventBase {
  event_id: string;
  session_id: string;
  client_time: string;
  base_control_epoch?: number;
  playback?: Partial<PlaybackTelemetry>;
}

export interface EnergyControlEvent extends ControlEventBase {
  kind: "relative_energy" | "absolute_energy";
  value: number;
}

export interface GenerationControlsEvent extends ControlEventBase {
  kind: "generation_controls";
  controls: Partial<LiveGenerationControls>;
  transition?: "phrase_morph";
}

export type ControlEvent = EnergyControlEvent | GenerationControlsEvent;

export interface ControlAcknowledgement {
  event_id: string;
  accepted: boolean;
  control_epoch: number;
  effective_target: number;
  effective_controls?: LiveGenerationControls;
  effective_from_sequence?: number;
  effective_from_ms?: number;
  duplicate?: boolean;
  reason?: string;
}

export interface SessionEnvelope<T = unknown> {
  protocol_version: typeof PROTOCOL_VERSION;
  message_id: string;
  session_id: string;
  type:
    | "session.resume"
    | "session.snapshot"
    | "control.submit"
    | "control.ack"
    | "timeline.update"
    | "session.degraded"
    | "playback.telemetry"
    | "error";
  sent_at: string;
  payload: T;
}

export interface SessionCreateRequest {
  genre?: MusicGenre;
  /** Legacy alias accepted during the prototype API transition. */
  style?: MusicGenre;
  mood?: MusicMood;
  palette?: InstrumentPalette;
  tempo_bpm?: Bpm;
  initial_energy: number;
  idempotency_key: string;
  client_instance_id: string;
}

export interface SessionCreateResponse {
  session_id: string;
  session_token: string;
  websocket_url: string;
  snapshot: SessionSnapshot;
}

export function envelope<T>(
  sessionId: string,
  type: SessionEnvelope<T>["type"],
  payload: T
): SessionEnvelope<T> {
  return {
    protocol_version: PROTOCOL_VERSION,
    message_id: crypto.randomUUID(),
    session_id: sessionId,
    type,
    sent_at: new Date().toISOString(),
    payload
  };
}

export function isUuid(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function parseControlEvent(value: unknown): ControlEvent {
  if (!value || typeof value !== "object") throw new TypeError("control event must be an object");
  const event = value as Partial<ControlEvent>;
  if (!isUuid(event.event_id) || !isUuid(event.session_id)) throw new TypeError("invalid event/session id");
  if (event.kind !== "relative_energy" && event.kind !== "absolute_energy" && event.kind !== "generation_controls") {
    throw new TypeError("invalid control kind");
  }
  if (event.kind === "generation_controls") {
    const controls = (event as Partial<GenerationControlsEvent>).controls;
    if (!controls || typeof controls !== "object" || Object.keys(controls).length === 0) throw new TypeError("generation controls are required");
    if (controls.genre !== undefined && !MUSIC_GENRES.includes(controls.genre)) throw new TypeError("unsupported genre");
    if (controls.mood !== undefined && !MUSIC_MOODS.includes(controls.mood)) throw new TypeError("unsupported mood");
    if (controls.palette !== undefined && !INSTRUMENT_PALETTES.includes(controls.palette)) throw new TypeError("unsupported palette");
    if (controls.tempo_bpm !== undefined && !isBpm(controls.tempo_bpm)) {
      throw new TypeError(`tempo_bpm must be one of ${BPM_VALUES.join(", ")}`);
    }
    if (controls.energy !== undefined && (!Number.isFinite(controls.energy) || controls.energy < 0 || controls.energy > 1)) {
      throw new TypeError("energy must be between 0 and 1");
    }
  } else {
    const energyEvent = event as Partial<EnergyControlEvent>;
    if (typeof energyEvent.value !== "number" || !Number.isFinite(energyEvent.value) || energyEvent.value < -1 || energyEvent.value > 1) {
      throw new TypeError("control value must be between -1 and 1");
    }
  }
  if (!event.client_time || Number.isNaN(Date.parse(event.client_time))) throw new TypeError("invalid client_time");
  return event as ControlEvent;
}

export function isEnvelope(value: unknown): value is SessionEnvelope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<SessionEnvelope>;
  return candidate.protocol_version === PROTOCOL_VERSION && isUuid(candidate.message_id) && isUuid(candidate.session_id) &&
    typeof candidate.type === "string" && typeof candidate.sent_at === "string";
}
